import type { TFunction } from 'i18next';
import type { TrapMarker } from '../../domain';
import type { Cluster } from './geo.utils';

const riskOrder = { high: 1, medium: 2, low: 3, not_applicable: 4, no_threshold: 5 };

export const sortRisksByPriority = (risks: [string, number][]) => {
  return risks.sort(([riskLevelA], [riskLevelB]) => {
    return riskOrder[riskLevelA as keyof typeof riskOrder] - riskOrder[riskLevelB as keyof typeof riskOrder];
  });
};

export const createClusterPopupContent = (cluster: Cluster, t: TFunction): string => {
  const count = cluster.markers.length;
  const displayCountLabel = (count: number) => t('pages.regional_overview.digital_traps.trap', { count });
  const riskCounts = sortRisksByPriority(Object.entries(cluster.riskCounts))
    .map(([riskLevel, count]) => {
      return `
      <span class="cluster-popup-pest-pressure-value">${displayCountLabel(count)}: <span class="risk ${riskLevel}">${t(
        `pages.regional_overview.digital_traps.${riskLevel}`
      )}</span></span>
    `;
    })
    .join('');

  return `
    <div class="cluster-popup-container">
      <span class="cluster-popup-count">${displayCountLabel(count)}</span>
      <div class="cluster-popup-content">
        <span class="cluster-popup-pest-pressure-label">${t(
          'pages.regional_overview.digital_traps.trap_details.7_day_pest_pressure'
        )}: </span>
        ${riskCounts}
      </div>
    </div>
  `;
};

export const createTrapPopupContent = (trap: TrapMarker, t: TFunction): string => {
  const riskLevelClass = `risk-${trap.popupRiskLevel.toLowerCase()}`;

  return `<div class="trap-popup-container">
    <div class="trap-popup-header">
      <div class="trap-popup-name">${trap.name}</div>
      <div class="trap-popup-id">#${trap.id}</div>
    </div>
    <div class="trap-popup-description">
      <div class="trap-popup-description-group pest-label">
        <div class="trap-popup-description-icon">
          <svg width='1em' height='1em' viewBox='0 0 24 24' fill='currentColor'>
            <path d='M11 17v-6h2v6h-2z' fill='currentColor' />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M17.93 12.998H21v2.01h-3.07c-.05.39-.12.77-.22 1.14l2.58 1.49-1 1.73-2.37-1.37c-1.08 1.81-2.88 3-4.92 3s-3.84-1.19-4.92-3l-2.37 1.35-1-1.73 2.58-1.49c-.1-.37-.17-.75-.22-1.14H3v-2h3.07c.05-.39.12-.77.22-1.14l-2.58-1.49 1-1.73 2.37 1.37c.28-.47.62-.9.99-1.29-.1-.55-.2-1.69.58-2.89L7 4.168l1.41-1.41 1.73 1.72c.63-.33 2.05-.89 3.73 0l1.72-1.72L17 4.168l-1.65 1.65c.78 1.2.68 2.33.58 2.89.37.38.71.81.99 1.29l2.37-1.36 1 1.73-2.58 1.49c.1.37.17.75.22 1.14zm-4.05-5.64c-.26-.79-1-1.36-1.88-1.36-.88 0-1.62.57-1.88 1.36.59-.23 1.22-.36 1.88-.36.66 0 1.29.13 1.88.36zM8 13.998c0 2.76 1.79 5 4 5s4-2.24 4-5-1.79-5-4-5-4 2.24-4 5z'
              fill='currentColor'
            />
          </svg>
        </div>
        <div class="trap-popup-description-text">${trap.pestName}</div>
      </div>
      <div class="trap-popup-description-group crop">
        <div class="trap-popup-description-text">${trap.cropName}</div>
      </div>
    </div>
    <div class="trap-popup-pest-pressure">
      <span class="trap-popup-pest-pressure-label">${t('pages.regional_overview.digital_traps.trap_details.7_day_pest_pressure')}: </span>
      <span class="trap-popup-tag ${riskLevelClass}"> ${t(`pages.regional_overview.digital_traps.${trap.popupRiskLevel}`)}: ${
    trap.popupCount
  }</span>
    </div>
  </div>`;
};

export const getPopupClassName = (riskLevel: string): string => {
  return `trap-popup risk-${riskLevel.toLowerCase()}`;
};
